import { addMonths, setMonth, startOfDay, startOfHour, startOfMinute, startOfMonth, startOfSecond, startOfWeek, } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { DateTime } from '../../DateTime';
import { validateArguments } from '../../SignatureValidation';
import * as Types from '../../Types';
import { ExprError } from '../ExprError';
import * as ExprTime from '../ExprTime';
export var DateTruncFn;
(function (DateTruncFn) {
    DateTruncFn.Name = `DATETRUNC`;
    DateTruncFn.Stage = "SCALAR";
    DateTruncFn.Signature = `DATETRUNC(<unit>, <datetime>, [<weekStart>])`;
    function make(callName, args, type, role) {
        return {
            name: DateTruncFn.Name,
            stage: DateTruncFn.Stage,
            data: { type, role },
            value: (...argVals) => {
                const [, dateValueNode] = args;
                const [datePart, dateValue, weekStartValue, yearStartValue] = argVals;
                const unit = DateTime.unit(datePart);
                if (unit == null) {
                    throw new Error(`Invalid date part specifier for DATETRUNC: ${datePart}`);
                }
                if (dateValue == null) {
                    return null;
                }
                if (typeof dateValue !== 'number') {
                    throw new Error(`Invalid date value for DATETRUNC: ${dateValue}`);
                }
                let dateMs = dateValue;
                switch (ExprTime.getTimeUnit(dateValueNode)) {
                    case "MICROS":
                        dateMs /= 1000;
                        break;
                    case "SECOND":
                        dateMs *= 1000;
                        break;
                    case "MILLIS":
                        dateMs *= 1;
                        break;
                }
                const zonedTime = utcToZonedTime(dateMs, 'Z');
                const truncTime = ((zonedTime) => {
                    var _a, _b, _c;
                    switch (unit) {
                        case "YEAR": {
                            const startMonth = DateTime.yearStartMonth((_a = DateTime.yearStart(yearStartValue)) !== null && _a !== void 0 ? _a : "JANUARY");
                            const date = addMonths(zonedTime, -startMonth + 1);
                            return startOfMonth(setMonth(date, startMonth - 1));
                        }
                        case "QUARTER": {
                            const month = zonedTime.getMonth() + 1;
                            const startMonth = DateTime.yearStartMonth((_b = DateTime.yearStart(yearStartValue)) !== null && _b !== void 0 ? _b : "JANUARY");
                            const distanceToStart = (month + 12 - startMonth) % 3;
                            return startOfMonth(addMonths(zonedTime, -distanceToStart));
                        }
                        case "MONTH":
                            return startOfMonth(zonedTime);
                        case "WEEK": {
                            return startOfWeek(zonedTime, {
                                weekStartsOn: DateTime.weekStartDow((_c = DateTime.weekStart(weekStartValue)) !== null && _c !== void 0 ? _c : "SUNDAY"),
                            });
                        }
                        case "DAYOFYEAR":
                        case "DAY":
                        case "WEEKDAY":
                            return startOfDay(zonedTime);
                        case "HOUR":
                            return startOfHour(zonedTime);
                        case "MINUTE":
                            return startOfMinute(zonedTime);
                        case "SECOND":
                            return startOfSecond(zonedTime);
                    }
                })(zonedTime);
                const utcResult = zonedTimeToUtc(truncTime, 'Z');
                return utcResult.getTime() * 1000;
            },
        };
    }
    DateTruncFn.make = make;
    function validate(callNode, callName, args) {
        return validateArguments({
            meta: { name: DateTruncFn.Name, signature: DateTruncFn.Signature, location: callNode.location },
            arguments: [
                {
                    customValidation: (unit) => {
                        const errors = [];
                        if (!(Types.isStringLiteral(unit) && DateTime.unit(unit.value))) {
                            const validUnits = DateTime.validUnits.join(`', '`);
                            const displayValue = Types.isLiteralLike(unit)
                                ? unit.value
                                : Types.getResolvedType(unit);
                            errors.push(new ExprError.InvalidArgument(DateTruncFn.Name, DateTruncFn.Signature, `expects <unit> to be one of '${validUnits}', saw: ` +
                                `<${displayValue}>`, callNode.location));
                        }
                        return errors;
                    },
                },
                {
                    customValidation: (arg) => {
                        switch (Types.getResolvedType(arg)) {
                            case "DATE_TIME":
                            case "TIMESTAMP":
                                return [];
                        }
                        return [
                            new ExprError.InvalidArgument(DateTruncFn.Name, DateTruncFn.Signature, `expected <datetime> to be an instance of either DATETIME or TIMESTAMP but received ${Types.getResolvedType(arg)}`, callNode.location),
                        ];
                    },
                },
                ...(args.length > 2
                    ? [
                        {
                            customValidation: validateWeekStart,
                        },
                    ]
                    : []),
                ...(args.length > 3
                    ? [
                        {
                            customValidation: validateYearStart,
                        },
                    ]
                    : []),
            ],
        }, args);
        function validateWeekStart(weekStart) {
            if (Types.isStringLiteral(weekStart) &&
                DateTime.weekStart(weekStart.value)) {
                return [];
            }
            const validWeekStarts = DateTime.validWeekStarts.join(`', '`);
            const displayValue = Types.isLiteralLike(weekStart)
                ? weekStart.value
                : Types.getResolvedType(weekStart);
            return [
                new ExprError.InvalidArgument(DateTruncFn.Name, DateTruncFn.Signature, `expected <weekStart> to be one of '${validWeekStarts}', saw: ` +
                    `<${displayValue}>`, callNode.location),
            ];
        }
        function validateYearStart(yearStart) {
            if (Types.isStringLiteral(yearStart) &&
                DateTime.yearStart(yearStart.value)) {
                return [];
            }
            const validYearStarts = DateTime.validYearStarts.join(`', '`);
            const displayValue = Types.isLiteralLike(yearStart)
                ? yearStart.value
                : Types.getResolvedType(yearStart);
            return [
                new ExprError.InvalidArgument(DateTruncFn.Name, DateTruncFn.Signature, `expected <yearStart> to be one of '${validYearStarts}', saw: ` +
                    `<${displayValue}>`, callNode.location),
            ];
        }
    }
    DateTruncFn.validate = validate;
})(DateTruncFn || (DateTruncFn = {}));
